/* eslint-disable react/jsx-handler-names */
import React, { type Dispatch, type MouseEvent, type SetStateAction, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { ClickElement, trackClubPromotionBannerClick } from '@jsmdg/tracking';
import {
    Breakpoint,
    Button,
    ButtonColor,
    ClubDiscountIcon,
    Dropdown,
    ExclamationTriangleIcon,
    Link,
    LinkVariant,
    PromotionBanner,
    PureButton,
    useBreakpoint,
} from '@jsmdg/yoshi';
import { type SelectOption } from '@jsmdg/yoshi/dist/types/selectOption';
import { ProductPromotionType, ProductType } from '../../../shared/enums';
import { isClusteredBox, isExperience, isValueVoucher } from '../../../shared/product-safeguards';
import {
    type CustomerState,
    type Price as PriceType,
    type ValueVoucher,
} from '../../../shared/types';
import { getLoginUrl } from '../../helper/BuyBoxUrlsUtil';
import { formatPrice } from '../../helper/formatPrice';
import { type UseBuyboxReturn } from '../../hooks/useBuybox';
import { useProduct } from '../../hooks/useProduct';
import { FeatureFlagVariant } from '../../types/enums/featureFlagVariant';
import { SellOption } from '../../types/enums/sellOption';
import { type ProductDetailContext } from '../../types/types';
import { AlternativeExperienceLink } from '../AlternativeExperienceLink/AlternativeExperienceLink';
import { BuyBoxSeal } from '../Buybox/BuyboxSeal';
import { FriendsDiscountBadge } from '../FriendsDiscountBadge';
import { FriendsDiscountSelect } from '../FriendsDiscountSelect';
import { PaybackPoints } from '../PaybackPoints';
import { Price } from '../Price';
import { StrikePriceInfo } from '../Price/StrikePriceInfo';
import { trackDropDownOpened } from '../ProductGroup/productGroup.tracking';
import { RedeemInfo } from '../RedeemInfo';
import { VoucherAndTicketSelector } from '../VoucherAndTicketSelector';
import styles from '../Buybox/Buybox.module.scss';

const LoadableShareContainer = loadable(async () => import('../ShareContainer/ShareContainer'));

const messages = defineMessages({
    friendsDiscountSelectHeader: {
        defaultMessage: 'Gutschein kaufen',
    },
    voucherAmount: {
        defaultMessage: 'Gutscheinbetrag',
    },
    promotionHeading: {
        defaultMessage: '-15%* {isMydays, select, true {mydays } other {}}Club Deal verfügbar',
    },
    promotionDescriptionLoggedIn: {
        defaultMessage:
            'Den Direktabzug erhältst {isMydays, select, true {Du} other {du}} als Member automatisch im Warenkorb',
    },
    promotionDescriptionNotLoggedIn: {
        defaultMessage:
            'Sichere {isMydays, select, true {Dir} other {dir}} {isMydays, select, true {Deinen} other {deinen}} Direktabzug im Warenkorb',
    },
    promotionDescriptionMobile: {
        defaultMessage: 'Direktabzug im Warenkorb',
    },
    promotionCta: {
        defaultMessage: 'Melde dich hier an',
    },
});
const DEFAULT_PRICE_VALUE_VOUCHER_RANGE = [
    20, 40, 50, 60, 80, 100, 120, 140, 150, 160, 180, 200, 250, 300, 350, 400, 500, 600,
];

const getPromotionBannerDescription = (customerState: CustomerState, isDesktop: boolean) => {
    const isLoggedIn = customerState?.isLoggedIn;

    if (isDesktop) {
        return isLoggedIn
            ? messages.promotionDescriptionLoggedIn
            : messages.promotionDescriptionNotLoggedIn;
    }

    return messages.promotionDescriptionMobile;
};

// eslint-disable-next-line complexity
const BuyboxBody = ({
    buyboxProps,
    callToActionText,
    handleBuyboxButtonClick,
    isCheckoutDisabled,
    isDiscountSelectOpen,
    setIsDiscountSelectOpen,
    setTargetElement,
}: {
    readonly setTargetElement: Dispatch<SetStateAction<HTMLElement | null>>;
    readonly setIsDiscountSelectOpen: Dispatch<boolean>;
    readonly isDiscountSelectOpen: boolean;
    readonly isCheckoutDisabled: boolean;
    readonly callToActionText: string;
    readonly buyboxProps: UseBuyboxReturn;
    readonly handleBuyboxButtonClick: () => void;
}) => {
    const {
        bookableLocationsCount,
        clusterType,
        handleSeatCountChange,
        handleValueVoucherPriceChange,
        hasFriendsDiscount,
        isProductPaybackCountable,
        purchaseType,
        seatCount,
    } = buyboxProps;
    const intl = useIntl();
    const product = useProduct();
    const upToSMBreakpoint = useBreakpoint(Breakpoint.SM);

    const {
        customerClubNamingEnabled,
        customerState,
        featureFlagsVariation,
        isBoxProductWithQrCodeLink,
        isMydays,
        redemptionInformation,
    } = useFragmentContext<ProductDetailContext>();

    const { type, url } = product;

    const buyboxIsSticky = featureFlagsVariation?.pdp_buybox_not_sticky !== FeatureFlagVariant.ON;
    const isDesktop = useBreakpoint(Breakpoint.XS);

    const promotionBannerHeading = intl.formatMessage(messages.promotionHeading, {
        isMydays,
    });
    const promotionBannerDescription = getPromotionBannerDescription(customerState, isDesktop);

    const preHandleSeatCountChange = async (
        newSeatCount: string,
        trackSelection = false,
    ): Promise<void> => {
        setIsDiscountSelectOpen(true);
        await handleSeatCountChange(newSeatCount, trackSelection);
        setIsDiscountSelectOpen(false);
    };

    const isValueVoucherFlexible = isValueVoucher(product) && product.priceHasRange;

    const createValueVoucherFlexibleItems = (productVV: ValueVoucher): SelectOption[] => {
        const min = productVV.priceRange?.min.amount as number;
        const max = productVV.priceRange?.max.amount as number;
        const currencyCode = productVV.priceRange?.min.currencyCode as string;
        return DEFAULT_PRICE_VALUE_VOUCHER_RANGE.filter(value => value >= min && value <= max).map(
            valueNumber => ({
                label: formatPrice(intl.locale, valueNumber, currencyCode),
                value: `${valueNumber}`,
            }),
        );
    };

    const flexibleValueVoucherItems = isValueVoucherFlexible
        ? createValueVoucherFlexibleItems(product)
        : [];

    const [valueVoucherSelectedItem, setValueVoucherSelectedItem] = useState(
        flexibleValueVoucherItems.length > 0 ? flexibleValueVoucherItems[0] : undefined,
    );

    const getCurrentPrice = (option?: SelectOption): PriceType => {
        return {
            gross: {
                amount: Number.parseFloat(option?.value as string),
                currencyCode: product.price.gross.currencyCode,
            },
        };
    };

    const handleBannerClick = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault();
        if (!window.location) {
            return;
        }

        if (!customerState?.isLoggedIn) {
            const href = `${window.origin}/login`;

            trackClubPromotionBannerClick(
                ClickElement.MemberPdpPromotion,
                promotionBannerHeading,
                href,
            );

            window.location.assign(getLoginUrl(href));
        }
    };

    product.price = isValueVoucherFlexible
        ? getCurrentPrice(valueVoucherSelectedItem)
        : product.price;
    product.originalPrice = isValueVoucherFlexible ? undefined : product.originalPrice;

    return (
        <div
            data-testid="stickyVoucherAndTicketSelectionWrapper"
            className={classNames(styles.voucherAndTicketSelectionWrapper, {
                'sticky-sm-top': isExperience(product) && buyboxIsSticky,
            })}
        >
            {isExperience(product) &&
                product.sellOption === SellOption.VoucherAndTicket &&
                Boolean(bookableLocationsCount) && (
                    <VoucherAndTicketSelector
                        buyboxProps={buyboxProps}
                        minParticipants={product.minParticipants}
                        maxParticipants={product.maxParticipants}
                        numberOfLocations={product.locations.length}
                        setDiscountSelectIsOpen={(isOpen: boolean) =>
                            setIsDiscountSelectOpen(isOpen)
                        }
                        isDiscountSelectOpen={isDiscountSelectOpen}
                    />
                )}
            {isExperience(product) &&
            !bookableLocationsCount &&
            product.sellOption === SellOption.TicketOnly ? (
                <div className="px-3x pt-3x px-xs-6x">
                    <strong className="d-flex">
                        <ExclamationTriangleIcon
                            key="exclamationTriangleTop"
                            className={styles.exclamationMark}
                        />
                        <FormattedMessage defaultMessage="Derzeit sind keine Termine verfügbar." />
                    </strong>
                </div>
            ) : null}
            {!isBoxProductWithQrCodeLink && (
                <>
                    {isExperience(product) &&
                        product.sellOption !== SellOption.TicketOnly &&
                        !bookableLocationsCount && (
                            <div className="pt-2x px-3x px-xs-6x">
                                {hasFriendsDiscount && <FriendsDiscountBadge className="pb-0-5x" />}
                                <FriendsDiscountSelect
                                    header={intl.formatMessage(
                                        messages.friendsDiscountSelectHeader,
                                    )}
                                    showRedeemInfo
                                    selectedSeatCount={seatCount}
                                    handleSeatCountChange={preHandleSeatCountChange}
                                    minParticipants={product.minParticipants}
                                    maxParticipants={product.maxParticipants}
                                    setDiscountSelectIsOpen={(isOpen: boolean) =>
                                        setIsDiscountSelectOpen(isOpen)
                                    }
                                    hasNoBookableLocations
                                    hasFriendsDiscount={hasFriendsDiscount}
                                />
                            </div>
                        )}
                    {type === ProductType.BOX && (
                        <div className="pt-3x px-3x px-xs-6x">
                            <RedeemInfo />
                        </div>
                    )}
                    {isValueVoucherFlexible && (
                        <div className="pt-3x px-3x px-xs-6x">
                            <Dropdown
                                label={intl.formatMessage(messages.voucherAmount)}
                                ariaLabel={intl.formatMessage(messages.voucherAmount)}
                                options={createValueVoucherFlexibleItems(product)}
                                value={valueVoucherSelectedItem?.value}
                                dataTestId="select-valuevoucher-flexible"
                                className="d-block"
                                overlayClasses={styles.box}
                                onClick={() => {
                                    trackDropDownOpened();
                                }}
                                onChange={(value: React.Key) => {
                                    const selectedElement = flexibleValueVoucherItems.find(
                                        item => item.value === value,
                                    );
                                    setValueVoucherSelectedItem(selectedElement);
                                    product.price = getCurrentPrice(selectedElement);
                                    handleValueVoucherPriceChange(product.price.gross);
                                }}
                            />
                        </div>
                    )}
                    <div className="pt-3x px-3x px-xs-6x">
                        <Price
                            price={product.price}
                            originalPrice={product.originalPrice}
                            multiplier={Number(seatCount) || 1}
                            vouchersTotal={redemptionInformation?.vouchersTotal}
                            hasFriendsDiscount={hasFriendsDiscount}
                            isDisabled={isDiscountSelectOpen}
                            purchaseType={purchaseType}
                            clusterType={clusterType}
                            isBoxCluster={isClusteredBox(product)}
                            minPrice={
                                product.type === ProductType.BOX ? product?.minPrice : undefined
                            }
                        />
                    </div>
                </>
            )}
            <div
                className="d-flex flex-wrap justify-content-space-between align-items-center py-3x px-3x px-xs-6x position-relative"
                data-testid="price-and-add-to-cart-button-wrapper"
            >
                <div
                    ref={setTargetElement as (el: HTMLElement | null) => void}
                    className={styles.sentinel}
                />
                <Button
                    data-testid="checkout-button"
                    onClick={handleBuyboxButtonClick}
                    disabled={isCheckoutDisabled}
                    className="w-100 text-nowrap"
                    color={ButtonColor.Brand}
                    a11yText={callToActionText}
                >
                    {callToActionText}
                </Button>
                <div className="d-sm-none w-100 mt-2x" data-testid="share-container">
                    <LoadableShareContainer productUrl={url} isDisabled={isDiscountSelectOpen} />
                </div>
                {!!redemptionInformation && <AlternativeExperienceLink />}
                {customerClubNamingEnabled &&
                    product.promotionType === ProductPromotionType.CUSTOMER_CLUB && (
                        <PureButton
                            onClick={handleBannerClick}
                            className={classNames('w-100', {
                                [styles.noPointerOnDisabled]: customerState.isLoggedIn,
                            })}
                            disabled={customerState.isLoggedIn}
                        >
                            <PromotionBanner
                                title={promotionBannerHeading}
                                description={
                                    <>
                                        {intl.formatMessage(promotionBannerDescription, {
                                            isMydays,
                                        })}{' '}
                                        <br />
                                        {!customerState?.isLoggedIn && (
                                            <Link
                                                variant={LinkVariant.Grey}
                                                href="/login"
                                                disabled={customerState?.isLoggedIn}
                                                onClick={handleBannerClick}
                                            >
                                                {intl.formatMessage(messages.promotionCta)}
                                            </Link>
                                        )}
                                    </>
                                }
                                className="mt-3x py-2x pl-2x w-100 justify-content-between"
                                icon={
                                    <ClubDiscountIcon
                                        size={70}
                                        className={classNames(
                                            styles.icon,
                                            'd-inline-block flex-shrink-0 align-self-baseline me-2x',
                                        )}
                                    />
                                }
                            />
                        </PureButton>
                    )}
                {isProductPaybackCountable && (
                    <PaybackPoints
                        price={product.price.gross.amount}
                        selectedSeatCount={Number(seatCount)}
                    />
                )}
            </div>
            <BuyBoxSeal />
            {upToSMBreakpoint && <StrikePriceInfo numTickets={Number(seatCount) || 1} />}
        </div>
    );
};

export { BuyboxBody };
